import {
  DefaultButton,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  IDragDropContext,
  IDragDropEvents,
  Label,
  mergeStyleSets,
  Selection,
  SelectionMode,
  Stack,
} from '@fluentui/react';
import React from 'react';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { popup } from '../../../../../addons/Popup';
import { RootState } from '../../../../../App/store';
import { addIcon, deleteIcon } from '../../../../../utils/icons';
import { theme } from '../../../../../utils/theme';
import { actions, getFormVariables } from '../../../slice';
import { getFormState, Product } from '../../../slice/newFormReducers';
import { connectRender, detailsListNoPaddingStyles } from '../utils';
import AmountInput from './AmountInput';
import BankInput from './BankInput';
import InstallmentsInput from './InstallmentsInput';
import Summary from './Summary';
import TypeInput from './TypeInput';

type PaymentListProps = {
  shipmentEditMode?: boolean;
  items: string[];
};

type PaymentListState = {
  deleteSelected: string[];
};

class PaymentList extends React.Component<PaymentListProps, PaymentListState> {
  private _columns: IColumn[] | undefined;
  private _dragDropEvents: IDragDropEvents | undefined;
  private _draggedItem: string | undefined;
  private selection: Selection;

  constructor(props: PaymentListProps) {
    super(props);

    this.state = {
      deleteSelected: [],
    };
    this.selection = new Selection({
      onSelectionChanged: () => {
        this.setState({
          deleteSelected: this.selection.getSelection() as string[],
        });
      },
    });
  }

  componentDidMount() {
    this.componentDidUpdate(this.props);
  }

  componentDidUpdate(prevProps: PaymentListProps) {
    if (this.props.items.length === 0) {
      addNewPaymentRow();
    }
  }

  render() {
    return (
      <Stack tokens={stackTokens}>
        {this.renderPaymentList()}
        {!this.props.shipmentEditMode && (
          <Stack horizontal>
            {this.renderButtons()}
            {this.renderSummary()}
          </Stack>
        )}
      </Stack>
    );
  }

  renderPaymentList() {
    return (
      <DetailsList
        items={this.props.items}
        columns={this.columns}
        selection={this.selection}
        selectionMode={SelectionMode.multiple}
        selectionZoneProps={{
          selection: this.selection,
          disableAutoSelectOnInputElements: true,
        }}
        layoutMode={DetailsListLayoutMode.fixedColumns}
        className={detailsListNoPaddingStyles}
        styles={{ root: { overflow: "hidden" } }}
      />
    );
  }

  renderSummary() {
    return (
      <div style={{ width: "35%", maxWidth: "600px" }}>
        <Summary />
      </div>
    );
  }

  renderButtons() {
    return (
      <Stack grow horizontal horizontalAlign="start" tokens={stackTokens}>
        <DefaultButton
          iconProps={addIcon}
          text="Satır Ekle"
          onClick={addNewPaymentRow}
        />
        <DefaultButton
          iconProps={deleteIcon}
          disabled={this.state.deleteSelected.length === 0}
          text={`${this.state.deleteSelected.length} Satırı Sil`}
          onClick={this.deleteSelectedRows.bind(this)}
        />
      </Stack>
    );
  }

  async deleteSelectedRows() {
    const { deleteSelected } = this.state;
    const { cancelled } = await popup({
      title: "Satırları Sil",
      message: `${deleteSelected.length} satırı silmek istediğinize emin misiniz?`,
      buttonText: "Sil",
    });

    if (cancelled) {
      return;
    }

    deleteSelected.forEach((uuid) => {
      actions.removeFormPayment(uuid);
    });
  }

  get columns() {
    if (this._columns) {
      return this._columns;
    }

    const cols: IColumn[] = [
      {
        key: "index",
        minWidth: 16,
        maxWidth: 16,
        name: "Index",
        iconName: "NumberSymbol",
        isIconOnly: true,
        onRender: (_, index) =>
          typeof index === "number" && <Label>{index + 1}</Label>,
      },
      {
        key: "type",
        minWidth: 16,
        maxWidth: 800,
        flexGrow: 6,
        name: "Ödeme Tipi",
        onRender: connectRender((uuid) => <TypeInput uuid={uuid} />),
      },
      {
        key: "amount",
        minWidth: 16,
        maxWidth: 800,
        flexGrow: 6,
        name: "Miktar",
        onRender: connectRender((uuid) => <AmountInput uuid={uuid} />),
      },
      {
        key: "bank",
        minWidth: 16,
        maxWidth: 800,
        flexGrow: 6,
        name: "Banka",
        onRender: connectRender((uuid) => <BankInput uuid={uuid} />),
      },
      {
        key: "installments",
        minWidth: 16,
        maxWidth: 800,
        flexGrow: 6,
        name: "Taksitlendirme",
        onRender: connectRender((uuid) => <InstallmentsInput uuid={uuid} />),
      },
      // {
      //   key: "paidCheck",
      //   minWidth: 16,
      //   maxWidth: 800,
      //   flexGrow: 3,
      //   name: "Ödendi",
      //   onRender: connectRender((uuid) => <PaymentCheckbox uuid={uuid} />),
      // },
    ];

    this._columns = cols
      .filter(
        (x) =>
          x.data?.displayOnShipmentEdit !== false ||
          !this.props.shipmentEditMode
      )
      .filter(
        (x) =>
          x.data?.displayOnShipmentEdit !== true || this.props.shipmentEditMode
      );

    return this._columns;
  }

  get dragDropEvents() {
    if (this._dragDropEvents) {
      return this._dragDropEvents;
    }

    this._dragDropEvents = {
      canDrop: (
        dropContext?: IDragDropContext,
        dragContext?: IDragDropContext
      ) => {
        return true;
      },
      canDrag: (uuid?: string, s?: any) => true,
      onDragEnter: (item?: string, event?: DragEvent) => styles.dragEnter,
      onDragLeave: (item?: string, event?: DragEvent) => {},
      onDrop: (item?: string, event?: DragEvent) => {
        if (item && this._draggedItem) {
          const formState = getFormState();
          if (!formState.products) return;

          const formVariables = getFormVariables();
          const _items = [...Object.keys(formVariables.products || {})].filter(
            (uuid) => uuid !== this._draggedItem
          );
          const dropItemIndex = _items.indexOf(item);

          _items.splice(dropItemIndex, 0, this._draggedItem);

          const newItems: { [uuid: string]: Product } = {};

          for (let i = 0; i < _items.length; i++) {
            const uuid = _items[i];
            newItems[uuid] = formState.products[uuid];
          }

          actions.setFormVariable(["products", newItems]);
        }
      },
      onDragStart: (
        item?: string,
        itemIndex?: number,
        selectedItems?: string[],
        event?: MouseEvent
      ) => {
        const moveIcon = document.getElementById(`rowMoveIcon-${item}`);

        if (moveIcon) {
          this._draggedItem = item;
        } else {
          this._draggedItem = undefined;
          event?.preventDefault();
        }
      },
      onDragEnd: (item?: string, event?: DragEvent) => {
        this._draggedItem = undefined;
      },
    };

    return this._dragDropEvents;
  }
}

export default connect((state: RootState) => {
  const fv = state.currentForm.newForms[state.currentForm.editingNewFormUUID];

  return {
    items: Object.keys(fv.payments || {}),
  };
})(PaymentList);

export const addNewPaymentRow = () => {
  const id = uuid();

  actions.setFormPaymentVariable([id, "uuid", id]);
  actions.setFormPaymentVariable([id, "paidAt", Date.now()]);
  actions.setFormPaymentVariable([id, "isPaid", true]);
};

const stackTokens = { childrenGap: 15 };
const styles = mergeStyleSets({
  dragEnter: {
    backgroundColor: theme.palette.neutralLight,
  },
});
